import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DocumentDTO } from '@almanac/shared';
import { DocumentEditDrawer } from '../components/DocumentEditDrawer.js';
import { DocumentListItem } from '../components/DocumentListItem.js';
import { DocumentPreview } from '../components/DocumentPreview.js';
import { LoadStatus } from '../components/LoadStatus.js';
import { PersonAvatar } from '../components/PersonAvatar.js';
import { VaultKindIcon } from '../components/VaultKindIcon.js';
import {
  documentMatchesQuery,
  groupVaultByKind,
  isRetiredRecord,
  isVaultRecord,
  useDocuments,
  vaultKindIconName,
  vaultReviewDue,
  vaultRowDate,
} from '../lib/documents.js';
import { useLifeAreas, useNowMinute } from '../lib/obligations.js';
import { usePeople } from '../lib/people.js';

/**
 * The Vault — vital records (passports, birth certificates, wills):
 * a second surface over the documents spine (documentCategory 'vital'),
 * organised by kind and person, never by expiry. Its jobs:
 *
 *  - findability under stress: person-first titles, flat kind groups,
 *    everything shared with the household by default;
 *  - the annual review: each record is confirmed current once a year
 *    ("Still current" on the card); lapsed ones surface in the banner
 *    and carry a "review due" accent on their row;
 *  - retirement: an expired passport with travel stamps stays a vault
 *    record, but drops into the collapsed Retired drawer (and moves
 *    under /Vault/Retired/ on Dropbox) so it's never mistaken for the
 *    current one.
 *
 * See PROJECT.md §Vault and DATA_MODEL.md §The vault.
 */
export function VaultPage() {
  const documents = useDocuments();
  const lifeAreas = useLifeAreas();
  const people = usePeople();
  const nowMs = useNowMinute();
  const now = useMemo(() => new Date(nowMs), [nowMs]);
  const navigate = useNavigate();

  const [preview, setPreview] = useState<DocumentDTO | null>(null);
  const [target, setTarget] = useState<DocumentDTO | 'new' | null>(null);
  const [retiredExpanded, setRetiredExpanded] = useState(false);
  // "Start review" narrows the list to just the lapsed records; the
  // filter exits by hand ("Show all") or by itself once nothing is due.
  const [reviewOnly, setReviewOnly] = useState(false);
  // The kind drilled into from the tile index (group key), or null on
  // the index itself. Search and review mode flatten past it.
  const [kindFilter, setKindFilter] = useState<string | null>(null);
  // Desktop rail person filter — one person at a time, tap to toggle.
  const [personFilter, setPersonFilter] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  function closeSearch() {
    setSearchOpen(false);
    setQuery('');
  }

  const peopleById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of people.data ?? []) map.set(p.id, p.displayName);
    return map;
  }, [people.data]);
  const areaById = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of lifeAreas.data ?? []) map.set(a.id, a.name);
    return map;
  }, [lifeAreas.data]);

  const allDocs = useMemo(() => documents.data ?? [], [documents.data]);
  const vaultDocs = useMemo(() => allDocs.filter(isVaultRecord), [allDocs]);
  const retiredDocs = useMemo(
    () => [...allDocs.filter(isRetiredRecord)].sort((a, b) => a.title.localeCompare(b.title)),
    [allDocs],
  );
  const dueCount = useMemo(
    () => vaultDocs.filter((d) => vaultReviewDue(d, now)).length,
    [vaultDocs, now],
  );

  // Auto-exit review mode once the last due record is confirmed.
  useEffect(() => {
    if (reviewOnly && dueCount === 0) setReviewOnly(false);
  }, [reviewOnly, dueCount]);

  const trimmed = query.trim();
  const visible = useMemo(() => {
    let docs = vaultDocs;
    if (reviewOnly) docs = docs.filter((d) => vaultReviewDue(d, now));
    if (personFilter) docs = docs.filter((d) => (d.pertainsTo ?? []).includes(personFilter));
    if (trimmed) {
      const q = trimmed.toLowerCase();
      docs = docs.filter(
        (d) =>
          documentMatchesQuery(d, trimmed) ||
          (d.pertainsTo ?? []).some((id) =>
            (peopleById.get(id) ?? '').toLowerCase().includes(q),
          ),
      );
    }
    return docs;
  }, [vaultDocs, reviewOnly, personFilter, trimmed, now, peopleById]);

  const groups = useMemo(() => groupVaultByKind(visible), [visible]);

  // Three ways to look at the vault: the tile index (landing), one
  // kind drilled into, or a flat list when searching / reviewing —
  // those two must cut across kinds, so they bypass the drill.
  const flatMode = reviewOnly || trimmed !== '';
  const drilled =
    !flatMode && kindFilter ? (groups.find((g) => g.key === kindFilter) ?? null) : null;
  const landing = !flatMode && !drilled;

  // A drilled kind can vanish under us (its last record deleted or
  // moved out) — fall back to the index instead of an empty screen.
  useEffect(() => {
    if (!flatMode && kindFilter && !groups.some((g) => g.key === kindFilter)) {
      setKindFilter(null);
    }
  }, [flatMode, kindFilter, groups]);

  // Rail: people with vault records, ordered by count.
  const railPeople = useMemo(() => {
    const counts = new Map<string, number>();
    for (const d of vaultDocs) {
      for (const id of d.pertainsTo ?? []) counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([id, count]) => ({ person: people.data?.find((p) => p.id === id), count }))
      .filter((e): e is { person: NonNullable<typeof e.person>; count: number } =>
        Boolean(e.person),
      )
      .sort((a, b) => b.count - a.count);
  }, [vaultDocs, people.data]);

  const sharedCount = vaultDocs.filter((d) => d.visibility === 'shared').length;

  function metaFor(doc: DocumentDTO): string {
    const about = (doc.pertainsTo ?? [])
      .map((id) => peopleById.get(id))
      .filter((n): n is string => Boolean(n))
      .join(', ');
    const expiry = doc.expiryDate
      ? `exp ${new Date(`${doc.expiryDate.slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}`
      : '';
    return [about, expiry].filter(Boolean).join(' · ');
  }

  const previewLive = preview
    ? (documents.data?.find((d) => d.id === preview.id) ?? preview)
    : null;
  const editingTarget =
    target && target !== 'new'
      ? (documents.data?.find((d) => d.id === target.id) ?? target)
      : target;

  return (
    <section>
      <header className="page-header page-header--with-action">
        <div className="page-header__lead">
          <button
            type="button"
            className="page-header__icon-button page-header__back"
            aria-label={drilled ? 'Back to the Vault' : 'Back to home'}
            onClick={() => (drilled ? setKindFilter(null) : navigate('/'))}
          >
            <BackIcon />
          </button>
          <div>
            <div className="section__label">{drilled ? 'Vault' : 'Vital records'}</div>
            <h2 className="page-header__title">{drilled ? drilled.label : 'Vault'}</h2>
          </div>
        </div>
        <button
          type="button"
          className={`page-header__icon-button ${searchOpen ? 'page-header__icon-button--active' : ''}`}
          onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
          aria-label={searchOpen ? 'Close search' : 'Search the vault'}
          aria-expanded={searchOpen}
        >
          <SearchIcon />
        </button>
      </header>

      {searchOpen && (
        <div className="obligations-search">
          <input
            ref={searchInputRef}
            type="search"
            className="obligations-search__input"
            placeholder="Search by title, person, or kind…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') closeSearch();
            }}
            aria-label="Search the vault"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <button
            type="button"
            className="obligations-search__clear"
            onClick={closeSearch}
            aria-label="Close search"
          >
            ×
          </button>
        </div>
      )}

      <LoadStatus
        isLoading={documents.isLoading}
        isError={documents.isError}
        error={documents.error}
      />

      <div className="page-columns">
      <div className="page-measure vault-groups">

      {dueCount > 0 && landing && (
        <div className="preview-expiry preview-expiry--banner">
          <span className="preview-expiry__text">
            Annual review — {dueCount} {dueCount === 1 ? 'record' : 'records'} not confirmed
            in over a year.
          </span>
          <span className="preview-expiry__actions">
            <button
              type="button"
              className="btn btn--primary btn--small"
              onClick={() => setReviewOnly(true)}
            >
              Start review
            </button>
          </span>
        </div>
      )}

      {reviewOnly && (
        <div className="preview-expiry preview-expiry--banner preview-expiry--handled">
          <span className="preview-expiry__text">
            Reviewing — open each record and confirm it&rsquo;s still current.
          </span>
          <span className="preview-expiry__actions">
            <button
              type="button"
              className="btn btn--secondary btn--small"
              onClick={() => setReviewOnly(false)}
            >
              Show all
            </button>
          </span>
        </div>
      )}

      {documents.isSuccess && vaultDocs.length === 0 && retiredDocs.length === 0 && (
        <p className="empty-state">
          Nothing in the vault yet. Add the identity-defining documents you&rsquo;d be
          devastated to lose — passports, birth certificates, wills — or open any
          existing document and choose <em>Move to Vault</em>.
        </p>
      )}

      {documents.isSuccess && vaultDocs.length > 0 && visible.length === 0 && (
        <p className="empty-state">
          {trimmed ? <>No matches for &ldquo;{trimmed}&rdquo;.</> : 'Nothing here.'}
        </p>
      )}

      {/* The index: one labelled-drawer tile per kind. */}
      {landing && groups.length > 0 && (
        <div className="vault-grid">
          {groups.map((group) => {
            const due = group.documents.filter((d) => vaultReviewDue(d, now)).length;
            const count = `${group.documents.length} ${group.documents.length === 1 ? 'record' : 'records'}`;
            return (
              <button
                key={group.key}
                type="button"
                className="vault-tile"
                onClick={() => setKindFilter(group.key)}
                aria-label={`${group.label} — ${count}${due > 0 ? `, ${due} to review` : ''}`}
              >
                {due > 0 && <span className="vault-tile__due" aria-hidden />}
                <span className="vault-tile__chip">
                  <VaultKindIcon name={vaultKindIconName(group.label)} />
                </span>
                <span className="vault-tile__body">
                  <span className="vault-tile__title">{group.label}</span>
                  <span className="vault-tile__sub">
                    {count}
                    {due > 0 && ` · ${due} to review`}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {landing && (
        <button type="button" className="documents-add" onClick={() => setTarget('new')}>
          <span className="documents-add__plus" aria-hidden>
            +
          </span>
          Add a vital record
        </button>
      )}

      {/* One kind, drilled into: its records as the familiar rows. */}
      {drilled && (
        <section>
          <div className="vault-kind-head">
            <span className="vault-kind-head__chip">
              <VaultKindIcon name={vaultKindIconName(drilled.label)} />
            </span>
            <div className="doc-seclabel">{drillMeta(drilled.documents)}</div>
          </div>
          <div className="doc-folders">
            {drilled.documents.map((d) => (
              <DocumentListItem
                key={d.id}
                document={d}
                meta={metaFor(d)}
                now={now}
                onOpen={setPreview}
                dateOverride={vaultRowDate(d, now)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Searching / reviewing cut across kinds: flat groups. */}
      {flatMode &&
        groups.map((group) => (
          <section key={group.key}>
            <div className="vault-kind-head">
              <span className="vault-kind-head__chip">
                <VaultKindIcon name={vaultKindIconName(group.label)} />
              </span>
              <div className="doc-seclabel">{group.label}</div>
            </div>
            <div className="doc-folders">
              {group.documents.map((d) => (
                <DocumentListItem
                  key={d.id}
                  document={d}
                  meta={metaFor(d)}
                  now={now}
                  onOpen={setPreview}
                  dateOverride={vaultRowDate(d, now)}
                />
              ))}
            </div>
          </section>
        ))}

      {retiredDocs.length > 0 && landing && (
        <section className="month-group" style={{ marginTop: '1.25rem' }}>
          <button
            type="button"
            className="month-group__heading month-group__heading--toggle"
            onClick={() => setRetiredExpanded((v) => !v)}
            aria-expanded={retiredExpanded}
          >
            <span
              className={`month-group__chevron ${retiredExpanded ? 'month-group__chevron--open' : ''}`}
              aria-hidden
            >
              ▸
            </span>
            Retired
            <span className="month-group__count">· {retiredDocs.length}</span>
          </button>
          {retiredExpanded && (
            <div className="doc-folders">
              {retiredDocs.map((d) => (
                <DocumentListItem
                  key={d.id}
                  document={d}
                  meta={metaFor(d)}
                  now={now}
                  onOpen={setPreview}
                  dateOverride={{ label: 'retired', warn: false }}
                />
              ))}
            </div>
          )}
        </section>
      )}

      </div>

      {/* Desktop-only rail (display:none below 840px). */}
      <aside className="page-rail">
        {railPeople.length > 0 && (
          <nav className="rail-card" aria-label="Filter by person">
            <div className="doc-seclabel">People</div>
            {railPeople.map(({ person, count }) => {
              const current = personFilter === person.id;
              return (
                <button
                  key={person.id}
                  type="button"
                  className={`doc-folder ${current ? 'doc-folder--current' : ''}`}
                  onClick={() => setPersonFilter(current ? null : person.id)}
                  aria-pressed={current}
                >
                  <PersonAvatar
                    person={person}
                    className="doc-folder__av doc-folder__av--person"
                  />
                  <span className="doc-folder__name">{person.displayName}</span>
                  <span className="doc-folder__count">{count}</span>
                </button>
              );
            })}
          </nav>
        )}

        {documents.isSuccess && (vaultDocs.length > 0 || retiredDocs.length > 0) && (
          <div className="rail-card">
            <div className="doc-seclabel">At a glance</div>
            <div className="rail-glance">
              <div className="rail-glance__line">
                <strong>{vaultDocs.length}</strong> vital{' '}
                {vaultDocs.length === 1 ? 'record' : 'records'}
              </div>
              {dueCount > 0 && (
                <div className="rail-glance__line">
                  <strong>{dueCount}</strong> due their annual review
                </div>
              )}
              {sharedCount > 0 && (
                <div className="rail-glance__line">
                  <strong>{sharedCount}</strong> shared with the household
                </div>
              )}
              {retiredDocs.length > 0 && (
                <div className="rail-glance__line">
                  <strong>{retiredDocs.length}</strong> retired
                </div>
              )}
            </div>
          </div>
        )}
      </aside>
      </div>

      {previewLive && (
        <DocumentPreview
          document={previewLive}
          lifeAreaName={areaById.get(previewLive.lifeAreaId)}
          onClose={() => setPreview(null)}
          onEdit={() => {
            const doc = previewLive;
            setPreview(null);
            setTarget(doc);
          }}
        />
      )}

      <DocumentEditDrawer
        vault
        target={editingTarget}
        onClose={() => setTarget(null)}
      />
    </section>
  );
}

/** The drilled kind's status line: count + how widely it's shared. */
function drillMeta(docs: DocumentDTO[]): string {
  const count = `${docs.length} ${docs.length === 1 ? 'record' : 'records'}`;
  const shared = docs.filter((d) => d.visibility === 'shared').length;
  if (shared === docs.length) return `${count} · shared with the household`;
  if (shared > 0) return `${count} · ${shared} shared`;
  return count;
}

function BackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15 18l-6-6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden>
      <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M13 13l4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
